/**
 * 延後審核對話框組件 | Delay Review Dialog Component
 *
 * 逐一顯示衝突工單，讓用戶審核並接受延後
 * Displays conflicting work orders one by one for user review and delay acceptance
 */

import { ConflictResult } from '@/types/order';

interface DelayReviewDialogProps {
  /** 當前要審核的衝突工單 | Current conflicting work order to review */
  conflict: ConflictResult;
  /** 當前索引（從 0 開始）| Current index (0-based) */
  currentIndex: number;
  /** 總衝突數量 | Total number of conflicts */
  total: number;
  /** 接受延後的回調 | Callback when accepting delay */
  onAccept: () => void;
  /** 拒絕延後的回調（可選）| Callback when rejecting delay (optional) */
  onReject?: () => void;
  /** 關閉對話框的回調 | Callback to close dialog */
  onClose?: () => void;
}

/**
 * 格式化 ISO 日期字串為台灣時間
 * Format ISO date string to Taiwan time
 */
function formatTaiwanDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 取得衝突類型的顯示樣式 | Get display style for conflict type
 */
function getConflictTypeBadge(type: 'resource' | 'time' | 'both'): string {
  switch (type) {
    case 'resource':
      return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    case 'time':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'both':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
}

/**
 * 取得衝突類型的中文顯示 | Get Chinese display for conflict type
 */
function getConflictTypeLabel(type: 'resource' | 'time' | 'both'): string {
  switch (type) {
    case 'resource':
      return '資源衝突';
    case 'time':
      return '時間衝突';
    case 'both':
      return '時間與資源衝突';
    default:
      return '未知';
  }
}

export function DelayReviewDialog({
  conflict,
  currentIndex,
  total,
  onAccept,
  onReject,
  onClose,
}: DelayReviewDialogProps) {
  const progress = ((currentIndex + 1) / total) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b-2 border-slate-200 px-6 py-5 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <span className="text-white text-lg">⚠️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">衝突工單審核</h3>
                <p className="text-xs text-slate-500">確認延後影響範圍</p>
              </div>
            </div>
            <span className={`px-4 py-2 text-sm font-bold rounded-xl border-2 shadow-sm ${getConflictTypeBadge(conflict.conflictType).replace('/20', '/20').replace('border-opacity-30', 'border-2')}`}>
              {getConflictTypeLabel(conflict.conflictType)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div
                className="bg-gradient-to-r from-blue-600 to-indigo-500 h-full transition-all duration-500 shadow-lg"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm font-bold text-slate-600 whitespace-nowrap bg-white px-3 py-1 rounded-lg border border-slate-200">
              {currentIndex + 1} / {total}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Work Order Info */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Work Order ID */}
              <div>
                <label className="text-xs text-[#64748b] uppercase tracking-wide">工單編號</label>
                <p className="text-sm font-mono text-[#0066cc] mt-1">{conflict.workOrderName}</p>
              </div>

              {/* Customer */}
              <div>
                <label className="text-xs text-[#64748b] uppercase tracking-wide">客戶</label>
                <p className="text-sm text-[#0f172a] mt-1">{conflict.customer}</p>
              </div>

              {/* Product Name */}
              <div className="col-span-2">
                <label className="text-xs text-[#64748b] uppercase tracking-wide">產品</label>
                <p className="text-sm text-[#0f172a] mt-1">{conflict.orderProductName}</p>
              </div>
            </div>
          </div>

          {/* Time Comparison */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 shadow-sm">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <span className="text-lg">⏱️</span>
              時間變更
            </h4>

            <div className="space-y-3">
              {/* Original Time */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs text-[#64748b] uppercase tracking-wide">原計畫時間</label>
                  <p className="text-sm text-[#64748b] mt-1">
                    {formatTaiwanDate(conflict.originalPlannedStart)} ~ {formatTaiwanDate(conflict.originalPlannedEnd)}
                  </p>
                </div>
                <svg className="w-5 h-5 text-[#ef4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center py-1">
                <div className="flex items-center gap-2 text-[#f59e0b]">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  <span className="text-sm font-medium">延後 {conflict.delayDays} 天</span>
                </div>
              </div>

              {/* New Time */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-xs text-[#64748b] uppercase tracking-wide">預估延後時間</label>
                  <p className="text-sm text-[#10b981] mt-1">
                    {formatTaiwanDate(conflict.estimatedDelayStart)} ~ {formatTaiwanDate(conflict.estimatedDelayEnd)}
                  </p>
                </div>
                <svg className="w-5 h-5 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Conflict Reason */}
          <div className="bg-gradient-to-br from-slate-50 to-zinc-50 border-2 border-slate-200 rounded-xl p-4 shadow-sm">
            <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
              <span className="text-lg">📋</span>
              衝突原因
            </h4>
            <p className="text-sm text-slate-700 leading-relaxed">{conflict.conflictReason}</p>
          </div>

          {/* Affected Resources */}
          {(conflict.affectedResources.persons.length > 0 ||
            conflict.affectedResources.machines.length > 0 ||
            conflict.affectedResources.materials.length > 0) && (
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl p-4 shadow-sm">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                <span className="text-lg">🔧</span>
                受影響資源
              </h4>

              <div className="grid grid-cols-3 gap-3">
                {/* Persons */}
                {conflict.affectedResources.persons.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-violet-200 shadow-sm">
                    <p className="text-xs font-bold text-slate-600 mb-2">人員 ({conflict.affectedResources.persons.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {conflict.affectedResources.persons.map((p) => (
                        <span key={p} className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-md border border-blue-300">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Machines */}
                {conflict.affectedResources.machines.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-violet-200 shadow-sm">
                    <p className="text-xs font-bold text-slate-600 mb-2">機台 ({conflict.affectedResources.machines.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {conflict.affectedResources.machines.map((m) => (
                        <span key={m} className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-violet-100 to-violet-200 text-violet-800 rounded-md border border-violet-300">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Materials */}
                {conflict.affectedResources.materials.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-violet-200 shadow-sm">
                    <p className="text-xs font-bold text-slate-600 mb-2">物料 ({conflict.affectedResources.materials.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {conflict.affectedResources.materials.map((m) => (
                        <span key={m} className="px-2 py-1 text-xs font-semibold bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-md border border-amber-300">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-slate-200 px-6 py-5 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              {currentIndex < total - 1 ? (
                <span className="text-slate-600">接受後將顯示下一個衝突工單</span>
              ) : (
                <span className="text-green-700 font-semibold flex items-center gap-2">
                  <span className="text-lg">✓</span>
                  這是最後一個衝突工單
                </span>
              )}
            </div>

            <div className="flex gap-3">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors rounded-lg hover:bg-slate-100"
                >
                  取消審核
                </button>
              )}

              {onReject && (
                <button
                  type="button"
                  onClick={onReject}
                  className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-red-600 to-rose-500 text-white rounded-xl hover:from-red-700 hover:to-rose-600 transition-all shadow-lg shadow-red-500/30 active:scale-95"
                >
                  拒絕延後
                </button>
              )}

              <button
                type="button"
                onClick={onAccept}
                className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl hover:from-blue-700 hover:to-indigo-600 transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2 active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                接受延後
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 審核完成對話框 | Review Complete Dialog
 * 顯示在最後一個衝突審核完成後 | Shown after last conflict is reviewed
 */
interface ReviewCompleteDialogProps {
  /** 接受的延後數量 | Number of accepted delays */
  acceptedCount: number;
  /** 總衝突數量 | Total number of conflicts */
  totalConflicts: number;
  /** 關閉回調 | Callback to close */
  onClose: () => void;
  /** 導航到甘特圖的回調（可選）| Callback to navigate to Gantt (optional) */
  onNavigateToGantt?: () => void;
}

export function ReviewCompleteDialog({
  acceptedCount,
  totalConflicts,
  onClose,
  onNavigateToGantt,
}: ReviewCompleteDialogProps) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl shadow-2xl max-w-md w-full">
        <div className="px-6 py-5 border-b-2 border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30">
              <span className="text-white text-2xl">✓</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">審核完成</h3>
              <p className="text-sm text-slate-600">所有衝突工單已處理完畢</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 text-center shadow-sm">
            <p className="text-slate-700 font-medium mb-3">
              已完成 {totalConflicts} 個衝突工單的審核
            </p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500">
                {acceptedCount}
              </p>
              <span className="text-2xl font-bold text-slate-400">/</span>
              <p className="text-2xl font-bold text-slate-600">{totalConflicts}</p>
            </div>
            <p className="text-sm font-semibold text-green-700 mt-2 flex items-center justify-center gap-1">
              <span>接受延後</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </p>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800 flex items-start gap-2">
              <span className="text-lg">💡</span>
              <span>可在甘特圖中查看預覽的新排程</span>
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-t-2 border-slate-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors rounded-lg hover:bg-slate-100"
          >
            關閉
          </button>
          {onNavigateToGantt && (
            <button
              type="button"
              onClick={onNavigateToGantt}
              className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl hover:from-blue-700 hover:to-indigo-600 transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2 active:scale-95"
            >
              前往甘特圖
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
